import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Briefcase, Mail, Lock, User, ArrowRight, Loader2,
  Eye, EyeOff, ShieldCheck, ChevronLeft, Globe, CheckCircle2
} from 'lucide-react';
import './RecruiterAuth.css';

const RecruiterAuth = () => {
  const [view, setView] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  // Login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPass, setShowLoginPass] = useState(false);

  // Register
  const [regUsername, setRegUsername] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showRegPass, setShowRegPass] = useState(false);

  // Forgot
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForgotPass, setShowForgotPass] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/recruiter/login`, {
        email: loginEmail,
        password: loginPassword
      });
      const { userId, name } = res.data;
      localStorage.setItem('recruiterId', userId);
      if (name) localStorage.setItem('recruiterName', name);
      setTimeout(() => {
        setIsLoading(false);
        navigate('/recruitment-dashboard');
      }, 900);
    } catch (error) {
      setIsLoading(false);
      alert(error.response?.data?.message || 'Login failed. Check credentials.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/recruiter/register`, {
        username: regUsername,
        email: regEmail,
        password: regPassword
      });
      setTimeout(() => {
        setIsLoading(false);
        alert(res.data.message || 'Registration successful!');
        setView('login');
      }, 900);
    } catch (error) {
      setIsLoading(false);
      alert(error.response?.data?.message || 'Registration failed');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("New password and confirm password do not match!");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/recruiter/change-password`, {
        email: forgotEmail,
        newPassword
      });
      setTimeout(() => {
        setIsLoading(false);
        alert('Password changed successfully!');
        setView('login');
        setForgotEmail(''); setNewPassword(''); setConfirmPassword('');
      }, 1200);
    } catch (error) {
      setIsLoading(false);
      alert('Failed to change password. Please try again.');
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const getMeta = () => {
    if (view === 'login') return { title: 'Recruiter Portal', sub: 'Access your hiring command center' };
    if (view === 'register') return { title: 'Join the Network', sub: 'Create your recruiter account' };
    return { title: 'Reset Credentials', sub: 'Securely update your access key' };
  };
  const meta = getMeta();

  return (
    <div className="rec-auth">
      <div
        className="rec-bg-image"
        style={{ transform: `translate3d(${mousePos.x * -10}px, ${mousePos.y * -6}px, 0) scale(1.05)` }}
      />
      <div className="rec-bg-overlay" />
      <div className="rec-bg-vignette" />
      <div
        className="rec-bg-mesh rec-mesh-1"
        style={{ transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)` }}
      />
      <div
        className="rec-bg-mesh rec-mesh-2"
        style={{ transform: `translate3d(${mousePos.x * -18}px, ${mousePos.y * -18}px, 0)` }}
      />

      <div className="rec-top-bar">
        <div className="rec-status">
          <span className="rec-pulse-dot" />
          <span>SECURE SESSION</span>
        </div>
        <div className="rec-top-meta">
          <span className="rec-time">{formatTime(currentTime)}</span>
          <span className="rec-dot">·</span>
          <span>{formatDate(currentTime)}</span>
          <span className="rec-dot">·</span>
          <span className="rec-region"><Globe size={11} /> DXB</span>
        </div>
      </div>

      <div className="rec-content">
        <div className="rec-brand-col">
          <div className="rec-brand-mark">
            <div className="rec-brand-icon">
              <Briefcase size={20} strokeWidth={2} />
            </div>
            <div className="rec-brand-text">
              <span className="rec-brand-name">HOTELPLACEMENT</span>
              <span className="rec-brand-sub">Recruiter Network</span>
            </div>
          </div>

          <h1 className="rec-headline">
            Scout the World's <br />
            <span className="rec-gold-text">Finest Talent</span>
          </h1>

          <p className="rec-subtext">
            Source, screen, and place exceptional hospitality professionals
            through a curated network built for serious recruiters.
          </p>

          <div className="rec-foot-meta">
            <div className="rec-foot-item">
              <CheckCircle2 size={13} />
              <span>Curated candidate pool</span>
            </div>
            <div className="rec-foot-item">
              <Lock size={13} />
              <span>End-to-end encryption</span>
            </div>
          </div>
        </div>

        <div className="rec-form-col">
          <div className="rec-panel">
            <div className="rec-panel-accent" />
            <div className="rec-panel-corner rec-c-tl" />
            <div className="rec-panel-corner rec-c-tr" />
            <div className="rec-panel-corner rec-c-bl" />
            <div className="rec-panel-corner rec-c-br" />

            <div className="rec-security-badge">
              <Lock size={10} />
              <span>TLS 1.3 SECURE</span>
            </div>

            <div className="rec-panel-header">
              <h2>{meta.title}</h2>
              <p>{meta.sub}</p>
            </div>

            {view === 'login' && (
              <form onSubmit={handleLogin} className="rec-form">
                <div className="rec-field">
                  <label>Work Email</label>
                  <div className="rec-input-wrap">
                    <Mail className="rec-input-icon" size={16} />
                    <input type="email" placeholder="recruiter@agency.com"
                      value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="rec-field">
                  <label>Password</label>
                  <div className="rec-input-wrap">
                    <Lock className="rec-input-icon" size={16} />
                    <input type={showLoginPass ? "text" : "password"} placeholder="••••••••••••"
                      value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
                    <button type="button" className="rec-eye-btn"
                      onClick={() => setShowLoginPass(!showLoginPass)} tabIndex={-1}>
                      {showLoginPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="rec-form-extras">
                  <button type="button" className="rec-forgot-link"
                    onClick={() => setView('forgot')}>
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="rec-submit" disabled={isLoading}>
                  <span className="rec-submit-bg" />
                  <span className="rec-submit-content">
                    {isLoading ? (
                      <><Loader2 className="rec-spin" size={17} /><span>Authenticating...</span></>
                    ) : (
                      <><span>Enter Dashboard</span><ArrowRight size={15} className="rec-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <div className="rec-admin-note">
                  <ShieldCheck size={13} />
                  <span>Account registration is managed by your administrator</span>
                </div>
              </form>
            )}

            {view === 'register' && (
              <form onSubmit={handleRegister} className="rec-form">
                <div className="rec-field rec-slide">
                  <label>Full Name</label>
                  <div className="rec-input-wrap">
                    <User className="rec-input-icon" size={16} />
                    <input type="text" placeholder="Your full name"
                      value={regUsername} onChange={(e) => setRegUsername(e.target.value)} required />
                  </div>
                </div>

                <div className="rec-field rec-slide" style={{ animationDelay: '60ms' }}>
                  <label>Email Address</label>
                  <div className="rec-input-wrap">
                    <Mail className="rec-input-icon" size={16} />
                    <input type="email" placeholder="you@agency.com"
                      value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="rec-field rec-slide" style={{ animationDelay: '120ms' }}>
                  <label>Password</label>
                  <div className="rec-input-wrap">
                    <Lock className="rec-input-icon" size={16} />
                    <input type={showRegPass ? "text" : "password"} placeholder="Create strong password"
                      value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required />
                    <button type="button" className="rec-eye-btn"
                      onClick={() => setShowRegPass(!showRegPass)} tabIndex={-1}>
                      {showRegPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="rec-submit" disabled={isLoading}>
                  <span className="rec-submit-bg" />
                  <span className="rec-submit-content">
                    {isLoading ? (
                      <><Loader2 className="rec-spin" size={17} /><span>Creating...</span></>
                    ) : (
                      <><span>Complete Registration</span><ArrowRight size={15} className="rec-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="rec-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Already have an account? <strong>Sign in</strong></span>
                </button>
              </form>
            )}

            {view === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="rec-form">
                <div className="rec-field rec-slide">
                  <label>Registered Email</label>
                  <div className="rec-input-wrap">
                    <Mail className="rec-input-icon" size={16} />
                    <input type="email" placeholder="Enter your email"
                      value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="rec-field rec-slide" style={{ animationDelay: '60ms' }}>
                  <label>New Password</label>
                  <div className="rec-input-wrap">
                    <Lock className="rec-input-icon" size={16} />
                    <input type={showForgotPass ? "text" : "password"} placeholder="New strong password"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                    <button type="button" className="rec-eye-btn"
                      onClick={() => setShowForgotPass(!showForgotPass)} tabIndex={-1}>
                      {showForgotPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="rec-field rec-slide" style={{ animationDelay: '120ms' }}>
                  <label>Confirm Password</label>
                  <div className="rec-input-wrap">
                    <Lock className="rec-input-icon" size={16} />
                    <input type={showForgotPass ? "text" : "password"} placeholder="Confirm new password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                  </div>
                </div>

                <button type="submit" className="rec-submit" disabled={isLoading}>
                  <span className="rec-submit-bg" />
                  <span className="rec-submit-content">
                    {isLoading ? (
                      <><Loader2 className="rec-spin" size={17} /><span>Updating...</span></>
                    ) : (
                      <><span>Update Credentials</span><ArrowRight size={15} className="rec-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="rec-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Back to <strong>Login</strong></span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruiterAuth;